import { useMemo } from "react";
import type { SkillHistory, PortfolioSnapshot } from "../lib/types";
import { useFilters } from "../store/filters";

interface Props {
  history: SkillHistory[];
  snapshots: PortfolioSnapshot[];
  liveSnapshot?: PortfolioSnapshot | null;
}

type TrendKey = "avg" | "above85" | "below70" | "broken";

const TABS: Array<{ key: TrendKey; label: string }> = [
  { key: "avg", label: "Avg" },
  { key: "above85", label: "≥85" },
  { key: "below70", label: "<70" },
  { key: "broken", label: "Broken" },
];

export function TrendStrip({ history, snapshots, liveSnapshot }: Props) {
  const { trend, setTrend, trendSource, setTrendSource } = useFilters();

  const portfolioData = useMemo(() => {
    const merged = liveSnapshot
      ? [...snapshots.filter((s) => s.generated !== liveSnapshot.generated), liveSnapshot]
      : snapshots;
    const slice = merged.slice(-12);
    return slice.map((snap, i) => {
      let value = 0;
      const skills = snap.skills ?? [];
      if (trend === "avg") value = snap.avgCombined;
      else if (trend === "above85") value = skills.filter((s) => s.combinedPercentage >= 85).length;
      else if (trend === "below70") value = skills.filter((s) => s.combinedPercentage < 70).length;
      else value = snap.broken ?? 0;
      return { runId: i + 1, value, label: snap.generated.split("T")[0] };
    });
  }, [snapshots, liveSnapshot, trend]);

  const autoresearchData = useMemo(() => {
    const allEntries = history.flatMap((h) => h.entries.map((e) => ({ ...e, skill: h.skill })));
    const grouped = new Map<number, typeof allEntries>();
    for (const e of allEntries) {
      if (!grouped.has(e.run)) grouped.set(e.run, []);
      grouped.get(e.run)!.push(e);
    }
    const runsGrouped = Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]).slice(-12);
    return runsGrouped.map(([runNumber, entries]) => {
      let value = 0;
      if (trend === "avg") {
        value = Math.round(entries.reduce((s, e) => s + e.value, 0) / entries.length);
      } else if (trend === "above85") {
        value = entries.filter((e) => e.value >= 85).length;
      } else if (trend === "below70") {
        value = entries.filter((e) => e.value < 70).length;
      } else {
        value = entries.filter((e) => e.status === "crash").length;
      }
      return { runId: runNumber, value, label: `run ${runNumber}` };
    });
  }, [history, trend]);

  const data = trendSource === "portfolio" && portfolioData.length > 0 ? portfolioData : autoresearchData;
  const max = data.length > 0 ? Math.max(1, ...data.map((d) => d.value)) : 1;
  const subtitle = trendSource === "portfolio"
    ? "portfolio snapshots (build:data)"
    : "autoresearch runs (legacy metric)";

  const bars = useMemo(() => data.map((d, i) => {
    const v = d.value;
    const h = Math.max(8, Math.round((v / max) * 100));
    const last = i === data.length - 1;
    const cls = trend === "broken" || trend === "below70"
      ? (v > max * 0.5 ? "bad" : v > max * 0.25 ? "warn" : "good")
      : (v > max * 0.66 ? "good" : v > max * 0.33 ? "warn" : "bad");
    return { h, last, cls, v, i, runId: d.runId, label: d.label };
  }), [data, max, trend]);

  return (
    <div className="border-t-[4px] border-fg p-4 bg-bg">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h4 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted font-medium">
          // Trends · {subtitle}
        </h4>
        <div className="flex flex-wrap gap-2">
          <div className="flex border border-fg" role="tablist" aria-label="Trend source">
            <button
              type="button"
              onClick={() => setTrendSource("portfolio")}
              aria-pressed={trendSource === "portfolio"}
              className={`font-mono text-[10px] uppercase px-2.5 py-1 border-r border-fg ${trendSource === "portfolio" ? "bg-fg text-bg" : ""}`}
            >
              Portfolio
            </button>
            <button
              type="button"
              onClick={() => setTrendSource("autoresearch")}
              aria-pressed={trendSource === "autoresearch"}
              className={`font-mono text-[10px] uppercase px-2.5 py-1 ${trendSource === "autoresearch" ? "bg-fg text-bg" : ""}`}
            >
              Autoresearch
            </button>
          </div>
          <div className="flex border border-fg" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTrend(t.key)}
                aria-pressed={trend === t.key}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border-r border-fg last:border-r-0 font-medium ${
                  trend === t.key ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-16" role="img" aria-label="Trend chart">
        {bars.length === 0 ? (
          <span className="font-mono text-[10px] text-muted uppercase">No data yet — run build:data</span>
        ) : (
          bars.map((b) => {
            const bgClass = b.cls === "good" ? "bg-good" : b.cls === "warn" ? "bg-warn" : "bg-bad";
            return (
              <div
                key={b.i}
                title={`${b.label}: ${b.v}`}
                className={`flex-1 min-w-[4px] ${b.last ? "bg-accent outline outline-1 outline-fg outline-offset-[2px]" : bgClass}`}
                style={{ height: `${b.h}%` }}
              />
            );
          })
        )}
      </div>
      {bars.length > 0 && (
        <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.1em] text-muted mt-2 font-medium">
          <span>{bars[0].label}</span>
          <span>{bars[bars.length - 1].label}</span>
        </div>
      )}
    </div>
  );
}